import React, { useState } from 'react';
import type { SearchParams } from '@/server/searchParams.ts';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import AddElementToLastWordInString from '@/app/common/components/Utility/AddElementToTheEndOfString';
import { areTaxesIncludedInPrice } from '@/app/common/config/configuration/configurationUtilsClient.ts';
import { SearchModes } from '@/domainLogic/index.ts';
import { Icon } from '@/app/common/icons/Icon';
import { IconWithTooltip } from '../../../../../Tooltip/styled';
import { TooltipType } from '../../../../../Tooltip/TooltipType';
import TaxesModal from '../../../../../Tooltip/Tooltips/TaxesModal';
import { TaxesLabel } from './styled';
import type { DealCard } from '../../../../../../utils/buildDealCards/dealCardTypes';
import BasicModalHeader from '../../../../../Modal/LayoutElements/ModalHeader';
import OpenXModal from '../../../../../Modal/OpenXModal';
import { formatPrice } from '../../../../../../utils/formatting';
import { TaxesIncludedBlock } from '../../PriceDetails/styled';

type Props = {
  isOverlay: boolean;
  taxesText: string | null;
  dealCard: DealCard;
  showTotalPrice: boolean;
  travellersCount: number;
  searchParams: SearchParams;
};

const Taxes = ({ taxesText, dealCard, isOverlay, showTotalPrice, searchParams, travellersCount }: Props) => {
  const { isMobile } = useCommonClient();
  const { searchInfoData, localization } = usePageWrapperData();
  const { country, configValuesApp } = localization;
  const isFamily = configValuesApp?.doublePriceFamilyEnabled ?? false;
  const { t, translateWithElements } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const searchMode = searchInfoData?.searchMode;
  const taxesIncluded = areTaxesIncludedInPrice({ searchMode, configValuesApp, country });
  const taxesTooltipTitle = t('vacation.oss.cards.taxes_tooltip.title');

  const isDP = searchMode === SearchModes.DP;
  const isDoublePricing = isDP && !!configValuesApp?.taxesIncludedDoublePricing;
  const rawPrice = dealCard?.product?.rate?.price?.price;
  const rawTaxes = dealCard?.product?.rate?.price?.taxes;
  const showDoublePricing = isDoublePricing && !!rawTaxes && rawTaxes > 0;

  const handleOnClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (isOverlay) return;
    e?.preventDefault();
    e?.stopPropagation();
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  if (showDoublePricing) {
    const isPerPerson = dealCard?.product?.rate?.price?.isPerPerson;
    const priceWithTaxes = (rawPrice ?? 0) + (rawTaxes ?? 0);
    // Family (has children): show group total. Adults-only: show per-person price.
    const totalPriceWithTaxes = isPerPerson && isFamily ? priceWithTaxes * travellersCount : priceWithTaxes;
    const formattedPriceWithTaxes = formatPrice({
      price: totalPriceWithTaxes,
      localization,
    });
    const amount = formattedPriceWithTaxes;
    const showPpSuffix = isPerPerson && !isFamily;
    const totalTaxes = isPerPerson && isFamily ? (rawTaxes ?? 0) * travellersCount : (rawTaxes ?? 0);
    const formattedTaxes = formatPrice({
      price: totalTaxes,
      localization,
    });
    const taxesIncludedLabel = translateWithElements(
      isOverlay
        ? 'vacation.oss.cards.taxes_double_pricing.includes_label.short'
        : 'vacation.oss.cards.taxes_double_pricing.includes_label',
      {
        amount: (
          <div key="taxes-included-amount" style={{ display: 'inline-flex', alignItems: 'center' }}>
            <strong>{amount}</strong>
            {showPpSuffix && <span>&nbsp;{t('vacation.oss.cards.pp')}</span>}
            {!isOverlay && (
              <Icon
                category="Semantics"
                name="icon-circle-info"
                size={14}
                color="iconPrimary"
                alt=""
                css="vertical-align: middle; margin-left: 2px;"
              />
            )}
          </div>
        ),
        ...(!isOverlay && {
          taxes: (
            <span key="taxes-included-taxes">
              {formattedTaxes}
              {showPpSuffix && <span>&nbsp;{t('vacation.oss.cards.pp')}</span>}
            </span>
          ),
        }),
      }
    );

    return (
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
        }}
      >
        <TaxesIncludedBlock $isOverlay={isOverlay} onClick={handleOnClick}>
          {taxesIncludedLabel}
        </TaxesIncludedBlock>
        <OpenXModal
          isOpen={showModal}
          onClose={handleCloseModal}
          fullScreen={false}
          id="taxes-modal-double-pricing"
          aria-label={taxesTooltipTitle}
          bodyBackgroundColor="backgroundPage"
          isMobile={isMobile}
          brand={localization.brand}
          header={{
            sticky: false,
            element: <BasicModalHeader handleClose={handleCloseModal}>{taxesTooltipTitle}</BasicModalHeader>,
          }}
        >
          <TaxesModal
            dealCard={dealCard}
            showTotalPrice={showTotalPrice}
            searchParams={searchParams}
            travellersCount={travellersCount}
          />
        </OpenXModal>
      </div>
    );
  }

  if (!taxesText) return null;

  const tooltipSize = 300;

  if (isOverlay) {
    return <span>{taxesText}</span>;
  }

  if (taxesIncluded) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === 'Space') {
            e.stopPropagation();
          }
        }}
      >
        <TaxesLabel onClick={(e) => handleOnClick(e)}>
          <AddElementToLastWordInString text={taxesText}>
            <IconWithTooltip
              tooltipSize={tooltipSize}
              tooltipType={TooltipType.primary}
              position="right"
              style={{ marginLeft: 2 }}
            >
              <Icon category="Semantics" name="icon-circle-info" size={16} color="iconPrimary" alt="" />
            </IconWithTooltip>
          </AddElementToLastWordInString>
        </TaxesLabel>
        <OpenXModal
          isOpen={showModal}
          onClose={handleCloseModal}
          fullScreen={false}
          id="taxes-modal"
          aria-label={taxesTooltipTitle}
          bodyBackgroundColor="backgroundPage"
          isMobile={isMobile}
          brand={localization.brand}
          header={{
            sticky: false,
            element: <BasicModalHeader handleClose={handleCloseModal}>{taxesTooltipTitle}</BasicModalHeader>,
          }}
        >
          <TaxesModal
            dealCard={dealCard}
            showTotalPrice={showTotalPrice}
            searchParams={searchParams}
            travellersCount={travellersCount}
          />
        </OpenXModal>
      </div>
    );
  }

  return <TaxesLabel>{taxesText}</TaxesLabel>;
};

export default Taxes;
